import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { styled } from 'styled-components';
import { isNotNil } from '@wistia/type-guards';
import { visuallyHiddenStyle } from '../../css/visuallyHiddenStyle';

const VisuallyHidden = styled.div({ ...visuallyHiddenStyle });

const VisuallyHiddenButFocusable = styled.div({
  '&:not(:focus-within)': visuallyHiddenStyle,
});

export type ScreenReaderOnlyProps = ComponentPropsWithoutRef<'div'> & {
  /**
   * Pass an arbitrary child node
   */
  children?: ReactNode;
  /**
   * Display content _only_ when it's focused.
   */
  focusable?: boolean;
  /**
   * The text that will appear to screenreaders but not be displayed on screen
   * (alternatively you can pass children)
   */
  text?: ReactNode;
};

/**
 * Used to hide content from the visual client, but keep it readable for screen readers.
 */
export const ScreenReaderOnly = ({
  text,
  children,
  focusable = false,
  ...props
}: ScreenReaderOnlyProps) => {
  const accessibleText = isNotNil(text) ? text : children;

  if (focusable) {
    return <VisuallyHiddenButFocusable {...props}>{accessibleText}</VisuallyHiddenButFocusable>;
  }

  return <VisuallyHidden {...props}>{accessibleText}</VisuallyHidden>;
};

ScreenReaderOnly.displayName = 'ScreenReaderOnly';
